<template>
    <div v-if="displayRegistration">
        <div v-if="signupConfig.displayMotivation" class="alert alert-primary" role="alert">
            <i class="bi bi-info-circle-fill me-2"></i>
            <span v-html="signupConfig.prompt"></span>
        </div>

        <div class="col-md-6 mx-auto">
            <text-input
                ref="emailInput" 
                v-model="emailModel" 
                description="Your email address" 
                :regex="Constants.RegularExpressions.Email" 
                :key-press-regex="Constants.RegularExpressions.EmailChars"
                maxlength="254">
            </text-input>

            <template v-if="signupConfig.requestPhoneNumber">
                <text-input
                    v-model="phoneNumberModel"
                    description="Your telephone number (this is optional)"
                    :regex="Constants.RegularExpressions.PhoneNumber"
                    :key-press-regex="Constants.RegularExpressions.PhoneNumberChars"
                    maxlength="20"
                    class="mt-2">
                </text-input>
            </template>
        
            <div v-if="signupConfig.showSecondaryPrompt" id="passwordHelpBlock" class="form-text">
                {{ signupConfig.secondaryPrompt }}
            </div>
        
            <div class="mx-auto text-center">
                <button ref="registerButton" class="btn btn-primary mt-2 ms-3 w-auto border" @click="onRegisterClick" :disabled="registering">{{ signupConfig.buttonText }}</button>
            </div>

            <div v-if="showRegistrationFailure" class="alert alert-primary mt-2" role="alert">
                <i class="bi bi-exclamation-circle-fill"></i>
                {{ registrationFailureMessage }}
            </div>
        </div>
    </div>

    <template v-if="hasStartedRegistrationProcess">
        <div class="alert alert-success" role="alert" v-html="signupConfig.completedMessage"></div>
        <template v-if="showDashboardAdvert">
            <p>
                You can see our new Dashboard, Track and Help page by clicking the button below.
            </p>
            <button class="btn btn-primary" @click="onGuiidePlusClick">Dashboard</button>
        </template>
    </template>

    <modal title="Email service provider issue" ref="emailServiceIssuesModal" id-key="emailServiceIssues"
        :showClose="false">
        <div class="flex-container">
            <p>Users tell us password setup and forgot password emails to BT addresses are not being received due to BT
                filters.
                To fix this, in your email:</p>
            <ol>
                <li>From the Settings tab, select the "Safe senders" folder and click on the Add button.</li>
                <li>Add the email address (contact@guiide.co.uk) you want to mark as safe and click Save.</li>
                <li>After a confirmation message the email address will be added to your list of safe senders.</li>
            </ol>

            <div class="buttons-wrapper mt-3 mb-3">
                <button type="button" class="btn btn-primary float-end" @click="onEmailServiceIssuesModalOk">Ok, I've fixed it</button>
            </div>
        </div>
    </modal>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { app } from "../../ts/app";
import { Retiree } from "@/guiide/ts/retiree";
import { useStore } from "vuex";
import { decodeEncodedHtml } from "../../ts/utilities";
import TextInput from "../common/TextInput.vue";
import Modal from "@/guiide/components/common/Modal.vue";
import HttpStatusCode from "@/shared/ts/httpStatusCode";
import Constants from "@/guiide/ts/constants";

const props = defineProps({
    retiree: {
        type: Object as () => Retiree | null,
        default: () => null,
    },
});

type SignUpConfig = { 
    canRegister: boolean, 
    prompt: string, 
    secondaryPrompt: string, 
    displayMotivation: boolean, 
    requestPhoneNumber: boolean, 
    completedMessage: string, 
    dashboardResource: string, 
    buttonText: string, 
    showSecondaryPrompt: boolean
};

const store = useStore();
const showRegistrationFailure = ref(false);
const registrationFailureMessage = ref("");
const emailServiceIssuesModal = ref(null);
const registering = ref(false);
const emailModel = ref("");
const emailInput = ref(null);
const phoneNumberModel = ref("");
const registerButton = ref(null);

function alert() {
    if (registerButton.value) {
        registerButton.value.classList.add('shake');
        setTimeout(() => {
            registerButton.value?.classList.remove('shake');
        }, 1000);
    }
}

const signupConfig = computed<SignUpConfig>(() => {
    const config = store.state.configuration;

    const decodedPrompt = decodeEncodedHtml(config.signupPrompt);
    const showSecondaryPrompt = config.signupSecondaryPrompt;

    return {
        canRegister: config.allowsSignup,
        prompt: decodedPrompt,
        secondaryPrompt: config.signupSecondaryPrompt,
        displayMotivation: config.signupPromptDisplayed,
        requestPhoneNumber: config.signupRequestsPhoneNumber,
        showSecondaryPrompt,
        buttonText: config.signupButtonText,
        completedMessage: config.signupCompletedMessage,
        dashboardResource: config.nav.track.resource
    };
});

const isRegistered = computed<boolean>(() => {
    return props.retiree?.isRegistered ?? true;
});

const displayRegistration = computed<boolean>(() => {
    return signupConfig.value.canRegister && !isRegistered.value && !hasStartedRegistrationProcess.value;
});

const showDashboardAdvert = computed<boolean>(() => {
    const config = store.state.configuration;
    return config.finishDisplaysNextButton && hasStartedRegistrationProcess.value;
});

const hasStartedRegistrationProcess = computed<boolean>(() => { return store.state.hasStartedRegistration; });

async function onRegisterClick() {
    if (!emailInput.value.isValid()) {
        return;
    }

    if (emailModel.value.includes("btinternet") || emailModel.value.includes("btopenworld")) {
        emailServiceIssuesModal.value.showModal();
        return;
    }

    await registerEmail();
}

async function onEmailServiceIssuesModalOk() {
    emailServiceIssuesModal.value.closeModal();
    await registerEmail();
}

async function registerEmail() {
    registering.value = true;
    showRegistrationFailure.value = false;
    registrationFailureMessage.value = "";

    const registrationResponse = await app.registerEmail(emailModel.value, phoneNumberModel.value);

    if (registrationResponse.status != HttpStatusCode.Ok) {
        showRegistrationFailure.value = true;
        registrationFailureMessage.value = registrationResponse.message;
    }

    registering.value = false;
}

function onGuiidePlusClick() {
    window.location.href = `/${signupConfig.value.dashboardResource}`;
}

defineExpose({
    alert
});
</script>

<style scoped>

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  50% { transform: translateX(5px); }
  75% { transform: translateX(-5px); }
}

.shake {
  animation: shake 0.5s;
}

</style>
